import { chunk, subscribeOnce } from '@tmx/core';
import { FleetApi, VehicleImport } from '@tmx/data-access';
import { Observable, pipe, switchMap } from 'rxjs';

import { VehiclesImportResponse } from '../../models/vehicles-import-response';
import { getErrorContext } from '../../utils/get-error-context';
import { reportToTrackjs } from '../../utils/report-to-trackjs';
import { ImportResponseEmitter } from '../models/import-response-emitter';
import { vehiclesImportResultsSchema } from '../schemas/vehicles-import-results.schema';
import { catalogVehiclesImportResponse } from './catalog-vehicles-import-response';

const CHUNK_SIZE = 1000;

/** chimport - CHunked IMPORT. 🐒 */
export function chimportVehicles(trackjsSource: string) {
  const emitter = new ImportResponseEmitter<VehiclesImportResponse>();
  const importResponse$ = new Observable<VehiclesImportResponse>(
    (subscriber) => {
      emitter.register(subscriber);
    }
  );

  const chimport = (vehicles: VehicleImport[]) => {
    const importResponse = new VehiclesImportResponse(0, 0, 0, [], 0, true);

    let responseCount = 0;
    const subsets = chunk(vehicles, CHUNK_SIZE);

    const emit = () => {
      responseCount += 1;
      if (responseCount === subsets.length) {
        emitter.emit(importResponse);
      }
    };

    for (const subset of subsets) {
      const recordUnrecoverableError = () => {
        importResponse.unrecoverable += subset.length;
        emit();
      };

      subscribeOnce(
        FleetApi.postVehiclesImport(subset),
        (response) => {
          for (const result of vehiclesImportResultsSchema.parse(response)) {
            catalogVehiclesImportResponse(
              result.validationResult,
              result.importResult,
              result.item.vehicleId,
              result.item,
              importResponse
            );
          }
          emit();
        },
        (error) => {
          reportToTrackjs(
            trackjsSource,
            'Error importing vehicles',
            getErrorContext(error)
          );

          if ((error as Response).status === 400) {
            const importResults = vehiclesImportResultsSchema.safeParse(
              (error as { error: string }).error
            );
            if (importResults.success) {
              for (const result of importResults.data) {
                catalogVehiclesImportResponse(
                  result.validationResult,
                  result.importResult,
                  result.item.vehicleId,
                  result.item,
                  importResponse
                );
              }
              emit();
            } else {
              recordUnrecoverableError();
            }
          } else {
            recordUnrecoverableError();
          }
        }
      );
    }
  };

  return pipe(
    switchMap(({ vehicles }: { vehicles: VehicleImport[] }) => {
      chimport(vehicles);
      return importResponse$;
    })
  );
}
